'use client';

import { useLayoutEffect } from 'react';

import { isInAppBrowser } from '@/app/utils/browser';

const HEIGHT_PROPERTY = '--spartan-hero-vh';
const HEIGHT_UNIT_PROPERTY = '--spartan-hero-vh-unit';
const LARGE_HEIGHT_PROPERTY = '--spartan-hero-lvh';

const measureViewportHeight = (unit: 'svh' | 'lvh') => {
  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:0',
    `height:100${unit}`,
    'visibility:hidden',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(probe);

  const height = probe.getBoundingClientRect().height;
  probe.remove();

  return (
    height || window.innerHeight || document.documentElement.clientHeight || 0
  );
};

interface SpartanStableViewportProps {
  sectionId: string;
}

export const SpartanStableViewport = ({
  sectionId,
}: SpartanStableViewportProps) => {
  useLayoutEffect(() => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const shouldIgnoreHeightOnlyResize = isInAppBrowser(navigator.userAgent);
    let viewportWidth = window.innerWidth;

    const updateHeight = () => {
      const height = measureViewportHeight('svh');
      section.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
      section.style.setProperty(HEIGHT_UNIT_PROPERTY, `${height * 0.01}px`);
      section.style.setProperty(
        LARGE_HEIGHT_PROPERTY,
        `${measureViewportHeight('lvh')}px`
      );
    };

    const handleResize = () => {
      if (shouldIgnoreHeightOnlyResize && window.innerWidth === viewportWidth)
        return;

      viewportWidth = window.innerWidth;
      updateHeight();
    };

    updateHeight();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      section.style.removeProperty(HEIGHT_PROPERTY);
      section.style.removeProperty(HEIGHT_UNIT_PROPERTY);
      section.style.removeProperty(LARGE_HEIGHT_PROPERTY);
    };
  }, [sectionId]);

  return null;
};
